// components/grid/GridIconAction.jsx
//
// ONE row action, rendered as its own icon button in an actions column — a
// drill-through, an edit, a remove, a delete. Every single-icon action column
// renders this cell (utils/gridConventions.ratchet.test.js).
// Wire it as an iconActionColumn's cellRenderer, with cellRendererParams
// `{ icon, label, onClick, when?, disabled?, destructive? }`.
//
// `icon` is the glyph COMPONENT from icons.js (`Package`, not
// `<Package … />`): this cell draws it at ICON.md, the size
// CONTROL_COLUMN_WIDTH is measured for (utils/gridDefaults.js).
//
// `label` is both the tooltip and the accessible name — an icon-only button has
// no other text. `onClick` receives the row.
//
// `when(row)` — optional — keeps the button off rows the action has nothing to
// act on (a batch row that failed, an item with no supplier). A row without
// the button is the merchant's cue that there is nothing to open; the row's
// other cells already say why. Same rule as ShortageTable's action column.
//
// `disabled` — optional — keeps the button on every row but refuses the
// click: an action the merchant may not take on this screen (an ERP owns the
// data). A missing button would read as a missing feature.
//
// `destructive` — optional — draws the button in the error colour, for an
// action that deletes a record.
//
// This cell is one action. A second action is either its own column — a
// conditional action is then a column that is present or absent, and the one
// every row has keeps the same one-click affordance on every store — or the
// actions share one multi-icon cluster column with its own renderer and
// `width` (Promotions' Apply / Edit / Delete).

import { IconButton, Tooltip, Box } from '@mui/material';
import { ICON } from '../../icons';

export default function GridIconAction({
  data, icon: Icon, label, onClick, when, disabled, destructive,
}) {
  // AG Grid renders group / pinned rows with no `data`; those are not merchant
  // rows and carry no action.
  if (!data) return null;
  if (when && !when(data)) return null;

  const button = (
    <IconButton
      size="small"
      color={destructive ? 'error' : 'default'}
      aria-label={label}
      disabled={disabled}
      onClick={() => onClick(data)}
    >
      <Icon size={ICON.md} />
    </IconButton>
  );

  return (
    // stopPropagation on mousedown so clicking the button never toggles row
    // selection (the grid-wide convention for in-cell controls).
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Tooltip title={label}>
        {/* MUI fires no pointer events on a disabled button, so its tooltip
            hangs off a wrapper. inline-flex: a plain span would put the
            button on a text line at the cell's line-height (.ag-cell), off
            centre. */}
        {disabled ? <Box component="span" sx={{ display: 'inline-flex' }}>{button}</Box> : button}
      </Tooltip>
    </Box>
  );
}
